import {useEffect, useRef, useState} from 'react';
import {HOST} from '../config';

// Strip markdown down to plain text for TTS — card content contains
// **bold**, bullet lists, and ![alt](img) refs (see markdownImage.js) that
// would otherwise be read aloud literally.
export function stripMarkdown(text) {
    if (!text) return '';
    return text
        .replace(/!\[[^\]]*]\([^)]*\)/g, '')
        .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
        .replace(/[*_`]{1,3}/g, '')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/^[-*+]\s+/gm, '')
        .replace(/^\d+\.\s+/gm, '')
        .replace(/\n+/g, '. ')
        .replace(/\s+/g, ' ')
        .trim();
}

// AirPods gesture -> action, per phase:
//   question phase:  next=reveal answer   prev=repeat question   play/pause=skip card
//   answer phase:     next=quick correct   prev=repeat answer     play/pause=go to grading
//   grading phase:    next=correct         prev=incorrect         play/pause=unsure
// All three phases end up calling onGrade/onSkip, which StudyMode already
// implements via updateCardStatus / a lightweight index-only advance.
export function useListenMode({enabled, card, voice, onGrade, onSkip}) {
    const [phase, setPhase] = useState('question');
    const [speaking, setSpeaking] = useState(false);
    const audioRef = useRef(null);
    const currentTextRef = useRef('');

    // Create the persistent <audio> element once.
    useEffect(() => {
        const audio = document.createElement('audio');
        audio.style.display = 'none';
        audio.playsInline = true;
        document.body.appendChild(audio);
        audioRef.current = audio;
        return () => {
            audio.pause();
            audio.remove();
            audioRef.current = null;
        };
    }, []);

    const playSilentLoop = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.dataset.silent === '1' && !audio.paused) return;
        audio.src = '/silence.wav';
        audio.loop = true;
        audio.dataset.silent = '1';
        audio.play().catch(() => {});
    };

    const speak = async (text) => {
        const audio = audioRef.current;
        if (!audio) return;
        currentTextRef.current = text;
        const cleanText = stripMarkdown(text);
        if (!cleanText) {
            playSilentLoop();
            return;
        }

        setSpeaking(true);
        try {
            const response = await fetch(`${HOST}/api/tts`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({text: cleanText, voice}),
            });
            if (!response.ok) throw new Error('TTS request failed');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            audio.loop = false;
            audio.dataset.silent = '0';
            audio.src = url;
            audio.onended = () => {
                URL.revokeObjectURL(url);
                setSpeaking(false);
                playSilentLoop();
            };
            await audio.play();
        } catch (error) {
            console.error(error);
            setSpeaking(false);
            playSilentLoop();
        }
    };

    const repeatCurrent = () => {
        if (currentTextRef.current) speak(currentTextRef.current);
    };

    // New card (or Listen Mode just turned on) -> reset to question phase and speak it.
    useEffect(() => {
        if (!enabled || !card) return;
        setPhase('question');
        speak(card.front);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, card?._id]);

    const handleNext = () => {
        if (phase === 'question') {
            setPhase('answer');
            speak(card.back);
        } else if (phase === 'answer') {
            onGrade('correct');
        } else if (phase === 'grading') {
            onGrade('correct');
        }
    };

    const handlePrevious = () => {
        if (phase === 'question' || phase === 'answer') {
            repeatCurrent();
        } else if (phase === 'grading') {
            onGrade('incorrect');
        }
    };

    // Shared by the AirPods play/pause action and an on-screen tap on the card.
    const handleTransport = () => {
        if (phase === 'question') {
            onSkip();
        } else if (phase === 'answer') {
            setPhase('grading');
        } else if (phase === 'grading') {
            onGrade('unsure');
        }
    };

    // MediaSession wiring — this is what routes AirPods taps to the handlers
    // above, and is what keeps the OS treating this page as "now playing"
    // (alongside the silent-loop fallback) so it still works with the screen locked.
    useEffect(() => {
        if (!enabled || !('mediaSession' in navigator)) return;

        navigator.mediaSession.metadata = new window.MediaMetadata({
            title: card ? stripMarkdown(phase === 'question' ? card.front : card.back).slice(0, 80) : 'Flashcards',
            artist: card?.theme || card?.topic || 'Flashcards',
        });
        navigator.mediaSession.setActionHandler('play', handleTransport);
        navigator.mediaSession.setActionHandler('pause', handleTransport);
        navigator.mediaSession.setActionHandler('nexttrack', handleNext);
        navigator.mediaSession.setActionHandler('previoustrack', handlePrevious);

        return () => {
            navigator.mediaSession.setActionHandler('play', null);
            navigator.mediaSession.setActionHandler('pause', null);
            navigator.mediaSession.setActionHandler('nexttrack', null);
            navigator.mediaSession.setActionHandler('previoustrack', null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, phase, card?._id]);

    // Toggling Listen Mode on is the user gesture that unlocks audio playback —
    // start the silent loop immediately, synchronously within that click.
    const activate = () => {
        playSilentLoop();
    };

    useEffect(() => {
        if (!enabled && audioRef.current) {
            audioRef.current.pause();
        }
    }, [enabled]);

    return {
        phase,
        flipped: enabled && phase !== 'question',
        speaking,
        activate,
        handleFlip: handleTransport,
    };
}
